import { ITableCol } from 'src/app/shared/swp-components/swp-table/swp-table.model';
import { TransportMode } from '@/models/transportMode';

export type QueryColsHandlers = {
  openEventsDialog?: (row: any) => void;
  openDocumentsDialog?: (row: any) => void;
  openInvoicesDialog?: (row: any) => void;
  openNotesDialog?: (row: any) => void;
  openContainersDialog?: (row: any, targetTab?: 'containers' | 'packages') => void;
  openExceptionsDialog?: (row: any) => void;
  getDateClass?: (actual?: Date | string, estimate?: Date | string) => string;
};

function normalizeModeOfTransport(value: any): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function normalizeContainerType(value: any): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function toCount(value: any): number {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.trunc(parsed);
}

function shouldUsePackageCount(row: any): boolean {
  const mode = normalizeModeOfTransport(row?.modeOfTransport);
  const containerType = normalizeContainerType(row?.containerType);

  if (mode === TransportMode.Air) return true;
  if (
    (mode === TransportMode.Sea || mode === TransportMode.Road) &&
    (containerType.includes('LCL') || containerType.includes('LTL'))
  )
    return true;

  return false;
}

function resolvePackageCount(row: any): number {
  const goodsLinesCount = toCount(row?.goodsLinesCount);
  if (goodsLinesCount > 0) return goodsLinesCount;

  return toCount(row?.totalPackages_Value);
}

function resolveContainerCount(row: any): number {
  return toCount(row?.transportUnitsCount);
}

function resolveContainerSectionCount(row: any): number {
  return shouldUsePackageCount(row) ? resolvePackageCount(row) : resolveContainerCount(row);
}

function resolveContainerSectionTargetTab(row: any): 'containers' | 'packages' {
  return shouldUsePackageCount(row) ? 'packages' : 'containers';
}

function withUniqueColumnIds(columns: ITableCol[]): ITableCol[] {
  const fieldCounts = new Map<string, number>();

  return columns.map((col) => {
    const explicitId = String(col.id ?? '').trim();
    if (explicitId) {
      return { ...col, id: explicitId };
    }

    const field = String(col.field ?? '').trim();
    const nextCount = (fieldCounts.get(field) ?? 0) + 1;
    fieldCounts.set(field, nextCount);

    // Keep backward compatibility for the first occurrence of each field.
    const id = nextCount === 1 ? field : `${field}__${nextCount}`;
    return { ...col, id };
  });
}

export function getShipmentColumnConfig(h: QueryColsHandlers = {}): ITableCol[] {
  return withUniqueColumnIds([
    {
      header: 'Events',
      field: 'statusesCount',
      type: 'numeric',
      actionsDef: [
        {
          svgKey: 'event',
          disabled: (x: any) => (x.statusesCount ?? 0) === 0,
          function: (x: any) => h.openEventsDialog?.(x),
          tooltip: 'Events',
          bendCount: (x: any) => x.statusesCount,
        },
      ],
      filterable: true,
    },
    {
      header: 'Documents',
      field: 'shipmentDocumentsCount',
      type: 'numeric',
      actionsDef: [
        {
          svgKey: 'documents',
          disabled: (x: any) => (x.shipmentDocumentsCount ?? 0) === 0,
          function: (x: any) => h.openDocumentsDialog?.(x),
          tooltip: 'documents',
          bendCount: (x: any) => x.shipmentDocumentsCount,
        },
      ],
      filterable: true,
    },
    {
      header: 'Invoices',
      field: 'shipmentInvoicesCount',
      type: 'numeric',
      actionsDef: [
        {
          svgKey: 'invoice',
          disabled: (x: any) => (x.shipmentInvoicesCount ?? 0) === 0,
          function: (x: any) => h.openInvoicesDialog?.(x),
          tooltip: 'invoices',
          bendCount: (x: any) => x.shipmentInvoicesCount,
        },
      ],
      filterable: true,
    },
    {
      header: 'Containers',
      field: 'transportUnitsCount',
      sortField: 'containersSort',
      type: 'numeric',
      sortable: true,
      actionsDef: [
        {
          svgKey: 'containers',
          disabled: (x: any) => resolveContainerSectionCount(x) === 0,
          function: (x: any) => h.openContainersDialog?.(x, resolveContainerSectionTargetTab(x)),
          tooltip: (x: any) =>
            resolveContainerSectionTargetTab(x) === 'packages' ? 'Packages' : 'Containers',
          bendCount: (x: any) => resolveContainerSectionCount(x),
          visible: () => true,
        },
      ],
      filterable: true,
    },
    {
      header: 'File Number',
      field: 'shipmentNumber',
      type: 'text',
      sortable: true,
      filterable: true,
      frozen: true,
    },
    {
      header: 'House Bill',
      field: 'housebillNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'House Bill Scope',
      field: 'housebillScope',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'HBL / MBL',
      field: 'hblMblSort',
      type: 'text',
      specialField: 'hblMblDualLine',
      definition: {
        line1Label: 'H:',
        line1ValueField: 'housebillNumber',
        line2Label: 'M:',
        line2ValueField: 'masterbillNumber',
        variant: 'codes',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Client Ref.',
      field: 'clientRef',
      type: 'text',
      sortable: true,
      filterable: true,
      cutLine: true,
      maxWidth: '100%',
      class: 'client-ref-small',
    },
    {
      header: 'Opened',
      field: 'houseBillissueDate',
      type: 'date',
      definition: {
        displayField: 'houseBillissueDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Transport Mode',
      field: 'modeOfTransport',
      type: 'text',
      specialField: 'transportModeBadge',
      sortable: true,
      filterable: true,
    },
    {
      header: 'ORIGIN / DEST',
      field: 'originDestination',
      type: 'text',
      specialField: 'originDestinationCell',
      definition: {
        originField: 'fullOrigin',
        destinationField: 'fullDestination',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'SHIPPER / CONSIGNEE',
      field: 'shipperConsignee',
      type: 'text',
      specialField: 'shipperConsigneeDualLine',
      definition: {
        line1ValueField: 'shipperName',
        line2ValueField: 'consigneeName',
        variant: 'names',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Origin',
      field: 'fullOrigin',
      type: 'text',
      tooltipField: 'originStationNameDisplay',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Origin Short Station',
      field: 'originShortStation',
      type: 'text',
      filterMatchMode: 'contains',
      specialField: 'stationShortCell',
      definition: {
        codeField: 'originShortStation',
        nameField: 'originStationName',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Origin Name',
      field: 'originStationNameDisplay',
      type: 'text',
      filterMatchMode: 'contains',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Origin Country Code',
      field: 'origin_CountryCode',
      type: 'text',
      tooltipField: 'originCountryNameDisplay',
      definition: {
        assistTableCode: 'COUNTRY_CODE',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Origin Country Name',
      field: 'originCountryNameDisplay',
      type: 'text',
      filterMatchMode: 'contains',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Destination',
      field: 'fullDestination',
      type: 'text',
      tooltipField: 'destinationStationNameDisplay',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Destination Short Station',
      field: 'destinationShortStation',
      type: 'text',
      filterMatchMode: 'contains',
      specialField: 'stationShortCell',
      definition: {
        codeField: 'destinationShortStation',
        nameField: 'destinationStationName',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Destination Name',
      field: 'destinationStationNameDisplay',
      type: 'text',
      filterMatchMode: 'contains',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Destination Country Code',
      field: 'destination_CountryCode',
      type: 'text',
      tooltipField: 'destinationCountryNameDisplay',
      definition: {
        assistTableCode: 'COUNTRY_CODE',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Destination Country Name',
      field: 'destinationCountryNameDisplay',
      type: 'text',
      filterMatchMode: 'contains',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Shipper',
      field: 'shipperName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Consignee',
      field: 'consigneeName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Exceptions',
      field: 'logisticExceptionsCount',
      type: 'numeric',
      sortable: true,
      actionsDef: [
        {
          svgKey: 'exceptions',
          function: (x: any) => h.openExceptionsDialog?.(x),
          tooltip: 'exceptions',
          bendCount: (x: any) => x.logisticExceptionsCount,
        },
        // {
        //   text: '🗸',
        //   color: 'green',
        //   tooltip: 'no exceptions',
        //   visible: (x: any) => x.logisticExceptionsCount == 0,
        // },
      ],
      filterable: true,
    },
    {
      header: 'ETD',
      field: 'etd',
      type: 'date',
      definition: {
        displayField: 'etdOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'ATD',
      field: 'atd',
      type: 'date',
      definition: {
        displayField: 'atdOriginal',
      },
      sortable: true,
      classFn: (x: any) => h.getDateClass?.(x?.atd, x?.etd) ?? '',
      filterable: true,
    },
    {
      header: 'ETA',
      field: 'eta',
      type: 'date',
      definition: {
        displayField: 'etaOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'ATA',
      field: 'ata',
      type: 'date',
      definition: {
        displayField: 'ataOriginal',
      },
      sortable: true,
      classFn: (x: any) => h.getDateClass?.(x?.ata, x?.eta) ?? '',
      filterable: true,
    },
    {
      header: 'Departure',
      field: 'departureSortDate',
      type: 'date',
      specialField: 'departureScheduleDateCell',
      definition: {
        estimatedLabel: 'ETD',
        actualLabel: 'ATD',
        estimatedDateResolver: (row: any) => row?.etdOriginal ?? row?.etd,
        actualDateResolver: (row: any) => row?.atdOriginal ?? row?.atd,
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Arrival',
      field: 'arrivalSortDate',
      type: 'date',
      specialField: 'arrivalScheduleDateCell',
      definition: {
        estimatedLabel: 'ETA',
        actualLabel: 'ATA',
        estimatedDateResolver: (row: any) => row?.etaOriginal ?? row?.eta,
        actualDateResolver: (row: any) => row?.ataOriginal ?? row?.ata,
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Last Event',
      field: 'milestoneLastEventLabel',
      filterField: 'milestoneLastEventFilterText',
      type: 'text',
      specialField: 'milestoneLastEventBadge',
      definition: {
        textField: 'milestoneLastEventLabel',
        eventDateField: 'milestoneLastEventDateOriginal',
        eventCodeField: 'milestoneLastEventCode',
        eventLocationField: 'milestoneLastEventLocation',
      },
      sortable: true,
      filterable: true,
      cutLine: true,
      maxWidth: '180px',
    },
    {
      header: 'Milestone Last Event Label',
      field: 'milestoneLastEventLabel',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Milestones',
      field: 'milestones',
      sortField: 'milestoneEventSort',
      type: 'milestone',
      definition: {
        pickupDateField: 'pickupDateOriginal',
        pickupCodeField: 'pickupCode',
        pickupLocationField: 'pickupLocation',
        departedDateField: 'departedDateOriginal',
        departedCodeField: 'departedCode',
        departedLocationField: 'departedLocation',
        arrivedDateField: 'arrivedDateOriginal',
        arrivedCodeField: 'arrivedCode',
        arrivedLocationField: 'arrivedLocation',
        deliveredDateField: 'deliveredDateOriginal',
        deliveredCodeField: 'deliveredCode',
        deliveredLocationField: 'deliveredLocation',
      },
      sortable: true,
      filterable: true,
    },
    // --- Milestones ---
    {
      header: 'Pickup Date',
      field: 'pickupDate',
      type: 'date',
      definition: {
        displayField: 'pickupDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Code',
      field: 'pickupCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Location',
      field: 'pickupLocation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Departed Date',
      field: 'departedDate',
      type: 'date',
      definition: {
        displayField: 'departedDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Departed Code',
      field: 'departedCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Departed Location',
      field: 'departedLocation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Arrived Date',
      field: 'arrivedDate',
      type: 'date',
      definition: {
        displayField: 'arrivedDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Arrived Code',
      field: 'arrivedCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Arrived Location',
      field: 'arrivedLocation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivered Date',
      field: 'deliveredDate',
      type: 'date',
      definition: {
        displayField: 'deliveredDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivered Code',
      field: 'deliveredCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivered Location',
      field: 'deliveredLocation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Milestone Last Event Date',
      field: 'milestoneLastEventDate',
      type: 'date',
      definition: {
        displayField: 'milestoneLastEventDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Milestone Last Event Code',
      field: 'milestoneLastEventCode',
      type: 'text',
      definition: {
        assistTableCode: 'STATUS_CODE',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Milestone Last Event Location',
      field: 'milestoneLastEventLocation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Request',
      field: 'pickupRequest',
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup',
      field: 'pickup',
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Customs',
      field: 'customs',
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
    {
      header: 'POD',
      field: 'pod',
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
    // --- Shipment info ---
    {
      header: 'Master Bill',
      field: 'masterbillNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Customs Brokerage File',
      field: 'customsBrokerageFile',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Container Type',
      field: 'containerType',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Shipment Type',
      field: 'shipmentType',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Agent Station',
      field: 'pickupAgentStation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivery Agent Station',
      field: 'deliveryAgentStation',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Controlling Agent Country',
      field: 'controllingAgentCountry',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    // --- Quantities ---
    {
      header: 'Total Packages',
      field: 'totalPackages_Value',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Total SLAC',
      field: 'totalSLAC_Value',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Total Weight',
      field: 'totalWeight_Value',
      type: 'numeric',
      numericFormat: 'weight',
      unitField: 'totalWeight_Unit',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Total Volume',
      field: 'totalVolume_Value',
      type: 'numeric',
      numericFormat: 'volume',
      unitField: 'totalVolume_Unit',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Total Chargeable',
      field: 'totalChargeable_Value',
      type: 'numeric',
      numericFormat: 'weight',
      unitField: 'totalChargeable_Unit',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Volume Weight',
      field: 'volumeWeight_Value',
      type: 'numeric',
      numericFormat: 'weight',
      unitField: 'volumeWeight_Unit',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Waybill Chargeable',
      field: 'waybillChargeable_Value',
      type: 'numeric',
      numericFormat: 'weight',
      unitField: 'waybillChargeable_Unit',
      sortable: true,
      filterable: true,
    },
    // --- Goods ---
    {
      header: 'Goods Value Currency',
      field: 'goodsValue_Currency',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Goods Value',
      field: 'goodsValue_Value',
      type: 'numeric',
      numericFormat: 'currency',
      unitField: 'goodsValue_Currency',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Goods Description',
      field: 'goodsDescription',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Mode of Transport',
      field: 'modeOfTransport',
      type: 'text',
      specialField: 'transportModeBadge',
      sortable: true,
      filterable: true,
    },
    // --- Terms / Bill ---
    {
      header: 'IncoTerms',
      field: 'incoTerms',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Carrier',
      field: 'carrier',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Additional Terms',
      field: 'additionalTerms',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'HBL Release Type',
      field: 'houseBillReleaseType',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'HBL Type',
      field: 'houseBillType',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Issue Date',
      field: 'issueDate',
      type: 'date',
      definition: {
        displayField: 'issueDateOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Service Code',
      field: 'serviceCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Product Code',
      field: 'productCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Service Level Code',
      field: 'serviceLevelCode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Security Inspection Type',
      field: 'securityInspectionType',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Customs Status',
      field: 'customsStatus',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Air Ratio',
      field: 'airRatio',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    // --- Dates ---
    {
      header: 'Transport Booking Requested',
      field: 'transportBookingRequested',
      type: 'date',
      definition: {
        displayField: 'transportBookingRequestedOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Estimated Pickup',
      field: 'estimatedPickup',
      type: 'date',
      definition: {
        displayField: 'estimatedPickupOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Estimated Delivery',
      field: 'estimatedDelivery',
      type: 'date',
      definition: {
        displayField: 'estimatedDeliveryOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'CFS Cut-Off',
      field: 'customerCFSCutOff',
      type: 'date',
      definition: {
        displayField: 'customerCFSCutOffOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'Docs Cut-Off',
      field: 'customerDocsCutOff',
      type: 'date',
      definition: {
        displayField: 'customerDocsCutOffOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'VGM Cut-Off',
      field: 'customerVGMCutOff',
      type: 'date',
      definition: {
        displayField: 'customerVGMCutOffOriginal',
      },
      sortable: true,
      filterable: true,
    },
    // --- Other ---
    {
      header: 'Lead Shipment HBL',
      field: 'leadShipmentHousebillNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Air Waybill Dim',
      field: 'airWaybillDim',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'HBL Delivery Mode',
      field: 'hBLDeliveryMode',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Virtual Pouch Accepted at Dest',
      field: 'virtualPouchAcceptedAtDest',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Special Handling',
      field: 'specialHandling',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'First BKC Vessel ETD',
      field: 'firstBKCVesselETD',
      type: 'date',
      definition: {
        displayField: 'firstBKCVesselETDOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'First BKC Vessel ETD Load',
      field: 'firstBKCVesselETDLoad',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'First BKC Vessel ETA',
      field: 'firstBKCVesselETA',
      type: 'date',
      definition: {
        displayField: 'firstBKCVesselETAOriginal',
      },
      sortable: true,
      filterable: true,
    },
    {
      header: 'First BKC Vessel ETA Disch',
      field: 'firstBKCVesselETADisch',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Confidential',
      field: 'confidential',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Cancelled',
      field: 'cancelled',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'First Gateway ETA',
      field: 'firstGatewayETA',
      type: 'date',
      definition: {
        displayField: 'firstGatewayETAOriginal',
      },
      sortable: true,
      filterable: true,
    },
    // --- Audit ---
    {
      header: 'Created At',
      field: 'createdAt',
      type: 'date',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Updated At',
      field: 'updatedAt',
      type: 'date',
      sortable: true,
      filterable: true,
    },
    // --- Counts ---
    {
      header: 'Goods Lines',
      field: 'goodsLinesCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'References',
      field: 'referencesCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Notes',
      field: 'notesCount',
      type: 'numeric',
      actionsDef: [
        {
          svgKey: 'notes',
          disabled: (x: any) => (x.notesCount ?? 0) === 0,
          function: (x: any) => h.openNotesDialog?.(x),
          tooltip: 'Notes',
          bendCount: (x: any) => x.notesCount,
        },
      ],
      sortable: true,
      filterable: true,
    },
    {
      header: 'Customs Entries',
      field: 'customsEntriesCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Orders',
      field: 'ordersCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Transport Legs',
      field: 'transportLegsCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Shipment Charges',
      field: 'shipmentChargesCount',
      type: 'numeric',
      sortable: true,
      filterable: true,
    },
    // --- Parties ---
    {
      header: 'Shipper Account',
      field: 'shipperAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Agent',
      field: 'pickupAgentName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Pickup Agent Account',
      field: 'pickupAgentAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Export Broker',
      field: 'exportBrokerName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Export Broker Account',
      field: 'exportBrokerAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Controlling Customer',
      field: 'controllingCustomerName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Controlling Customer Account',
      field: 'controllingCustomerAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Prepaid Bill To Party',
      field: 'prepaidBillToPartyName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Prepaid Bill To Party Account',
      field: 'prepaidBillToPartyAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Consignee Account',
      field: 'consigneeAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivery Agent',
      field: 'deliveryAgentName',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Delivery Agent Account',
      field: 'deliveryAgentAccountNumber',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    // --- Completion ---
    {
      header: 'Completion Rule Date',
      field: 'completionRuleDate',
      type: 'date',
      sortable: true,
      filterable: true,
    },
    {
      header: 'Follow Up Completed',
      field: 'followUpCompleted',
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
  ]);
}

/**
 * Return an array of simple header descriptors derived from the full column defs.
 * Each item contains { key, displayName } for use in lightweight UIs or selects.
 */
export function getShipmentProfileColumns(): { key: string; displayName: string }[] {
  return getShipmentColumnConfig().map((c) => ({ key: c.id ?? c.field, displayName: c.header }));
}

export function getShipmentProfileDateColumns(): string[] {
  return getShipmentColumnConfig()
    .filter((c) => c.type === 'date')
    .map((c) => c.field);
}
